import React, { useState, useEffect } from "react";
import { getAuth } from "../Config/matuAuth";
import { getDatabase, ref, get } from "../Config/matuRtdb";

const AuthDebugger = () => {
  const [authInfo, setAuthInfo] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        const authInfo = {
          isAuthenticated: !!user,
          user: user ? {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            emailVerified: user.emailVerified,
            providerData: user.providerData
          } : null,
          localStorage: {
            userUUID: localStorage.getItem("userUUID"),
            uid: localStorage.getItem("uid"),
            rememberedEmail: localStorage.getItem("rememberedEmail")
          }
        };

        setAuthInfo(authInfo);

        // Si hay un usuario autenticado o UID en localStorage, buscar datos
        const uid = user?.uid || localStorage.getItem("userUUID") || localStorage.getItem("uid");
        
        if (uid) {
          const db = getDatabase();
          
          // Buscar en usuarios
          const userRef = ref(db, `usuarios/${uid}`);
          const snapshot = await get(userRef);
          
          if (snapshot.exists()) {
            setUserData({
              path: `usuarios/${uid}`,
              data: snapshot.val()
            });
          } else {
            // Buscar en BetPay
            const betpayRef = ref(db, `BetPay/${uid}`);
            const betpaySnapshot = await get(betpayRef);
            
            if (betpaySnapshot.exists()) {
              setUserData({
                path: `BetPay/${uid}`,
                data: betpaySnapshot.val()
              });
            } else {
              setUserData({
                path: null,
                data: null,
                error: "Usuario no encontrado en ninguna ubicación"
              });
            }
          }
        }
      } catch (error) {
        console.error("Error en AuthDebugger:", error);
        setUserData({
          path: null,
          data: null,
          error: error.message
        });
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const handleSignOut = async () => {
    try {
      const auth = getAuth();
      await auth.signOut();
      localStorage.removeItem("userUUID");
      localStorage.removeItem("uid");
      window.location.reload();
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const handleClearLocalStorage = () => {
    localStorage.removeItem("userUUID");
    localStorage.removeItem("uid");
    localStorage.removeItem("rememberedEmail");
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-blue-200">Cargando información de depuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 mb-6">
          <h1 className="text-3xl font-bold text-white mb-6">🔍 Debug de Autenticación</h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Información de Firebase Auth */}
            <div className="bg-white/5 rounded-xl p-4">
              <h2 className="text-xl font-bold text-white mb-4">🔥 Firebase Auth</h2>
              <div className="space-y-2">
                <p className="text-white/80">
                  <span className="font-semibold">Estado:</span>{" "}
                  <span className={authInfo?.isAuthenticated ? "text-green-400" : "text-red-400"}>
                    {authInfo?.isAuthenticated ? "✅ Autenticado" : "❌ No autenticado"}
                  </span>
                </p>
                {authInfo?.user && (
                  <>
                    <p className="text-white/80">
                      <span className="font-semibold">UID:</span> {authInfo.user.uid}
                    </p>
                    <p className="text-white/80">
                      <span className="font-semibold">Email:</span> {authInfo.user.email}
                    </p>
                    <p className="text-white/80">
                      <span className="font-semibold">Nombre:</span> {authInfo.user.displayName || "N/A"}
                    </p>
                    <p className="text-white/80">
                      <span className="font-semibold">Email verificado:</span>{" "}
                      <span className={authInfo.user.emailVerified ? "text-green-400" : "text-red-400"}>
                        {authInfo.user.emailVerified ? "✅ Sí" : "❌ No"}
                      </span>
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Información de localStorage */}
            <div className="bg-white/5 rounded-xl p-4">
              <h2 className="text-xl font-bold text-white mb-4">💾 localStorage</h2>
              <div className="space-y-2">
                <p className="text-white/80">
                  <span className="font-semibold">userUUID:</span>{" "}
                  <span className={authInfo?.localStorage?.userUUID ? "text-green-400" : "text-red-400"}>
                    {authInfo?.localStorage?.userUUID || "No encontrado"}
                  </span>
                </p>
                <p className="text-white/80">
                  <span className="font-semibold">uid:</span>{" "}
                  <span className={authInfo?.localStorage?.uid ? "text-green-400" : "text-red-400"}>
                    {authInfo?.localStorage?.uid || "No encontrado"}
                  </span>
                </p>
                <p className="text-white/80">
                  <span className="font-semibold">rememberedEmail:</span>{" "}
                  <span className={authInfo?.localStorage?.rememberedEmail ? "text-green-400" : "text-red-400"}>
                    {authInfo?.localStorage?.rememberedEmail || "No encontrado"}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Información de Base de Datos */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 mb-6">
          <h2 className="text-2xl font-bold text-white mb-4">🗄️ Base de Datos</h2>
          
          {userData?.error ? (
            <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4">
              <p className="text-red-300 font-semibold">❌ Error:</p>
              <p className="text-red-200">{userData.error}</p>
            </div>
          ) : userData?.data ? (
            <div className="space-y-4">
              <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4">
                <p className="text-green-300 font-semibold">✅ Usuario encontrado en:</p>
                <p className="text-green-200 font-mono">{userData.path}</p>
              </div>
              
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="text-lg font-bold text-white mb-3">📊 Datos del Usuario</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/80">
                      <span className="font-semibold">Nombre:</span> {userData.data.name || "N/A"}
                    </p>
                    <p className="text-white/80">
                      <span className="font-semibold">Email:</span> {userData.data.email || "N/A"}
                    </p>
                    <p className="text-white/80">
                      <span className="font-semibold">Tipo de cuenta:</span> {userData.data.tipoCuenta || "N/A"}
                    </p>
                    <p className="text-white/80">
                      <span className="font-semibold">isSuperAdmin:</span>{" "}
                      <span className={userData.data.isSuperAdmin ? "text-green-400" : "text-red-400"}>
                        {String(userData.data.isSuperAdmin)} ({typeof userData.data.isSuperAdmin})
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-white/80">
                      <span className="font-semibold">Fecha de registro:</span> {userData.data.fechaRegistro || "N/A"}
                    </p>
                    <p className="text-white/80">
                      <span className="font-semibold">Último acceso:</span> {userData.data.ultimoAcceso || "N/A"}
                    </p>
                    <p className="text-white/80">
                      <span className="font-semibold">Estado:</span> {userData.data.estado || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4">
              <p className="text-yellow-300 font-semibold">⚠️ No hay datos de usuario para mostrar</p>
              <p className="text-yellow-200">Esto puede indicar que no hay un usuario autenticado o no se encontró en la base de datos.</p>
            </div>
          )}
        </div>

        {/* Acciones */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h2 className="text-2xl font-bold text-white mb-4">⚡ Acciones</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleSignOut}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              🔓 Cerrar Sesión
            </button>
            <button
              onClick={handleClearLocalStorage}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              🗑️ Limpiar localStorage
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              🔄 Recargar Página
            </button>
            <a
              href="/super-admin-dashboard"
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors inline-block"
            >
              🚀 Ir a Super Admin Dashboard
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthDebugger; 